import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import * as Icon from 'react-feather';

const dropItem = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  background: 'none',
  border: 'none',
  padding: '8px 16px',
  fontSize: '0.85rem',
  color: '#334155',
  cursor: 'pointer',
  textAlign: 'left',
};

function TopMenu({ onSidebarToggle }) {
  const myContext = useAuth();
  const navigate = useNavigate();

  // ── Avatar dropdown ───────────────────────────────────────
  const [avatarOpen, setAvatarOpen] = useState(false);
  const avatarRef = useRef(null);

  // ── Workspace dropdown ────────────────────────────────────
  const [wsOpen, setWsOpen] = useState(false);
  const wsRef = useRef(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [currentWs, setCurrentWs] = useState(
    () => JSON.parse(localStorage.getItem('sp_workspace') || 'null')
  );

  useEffect(() => {
    fetch('https://api.schedpilot.com/wp-json/api/v1/workspaces', {
      credentials: 'include',
    })
      .then(r => r.json())
      .then(data => {
        if (!Array.isArray(data)) return;
        setWorkspaces(data);
        // If nothing stored yet, default to the first workspace
        if (!currentWs && data.length > 0) {
          setCurrentWs(data[0]);
          localStorage.setItem('sp_workspace', JSON.stringify(data[0]));
        }
      })
      .catch(() => {});
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const selectWorkspace = (ws) => {
    setCurrentWs(ws);
    localStorage.setItem('sp_workspace', JSON.stringify(ws));
    setWsOpen(false);
  };

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (avatarRef.current && !avatarRef.current.contains(e.target)) setAvatarOpen(false);
      if (wsRef.current && !wsRef.current.contains(e.target)) setWsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = async () => {
    try {
      await fetch('https://api.schedpilot.com/wp-json/api/v1/logout', {
        method: 'GET',
        credentials: 'include',
      });
    } catch {}
    localStorage.removeItem('myLoggedInUser2');
    localStorage.removeItem('sp_workspace');
    window.location.href = '/';
  };

  return (
    <nav style={{
      background: '#fff',
      borderBottom: '1px solid #d1d9e0',
      padding: '0 20px',
      height: 56,
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      flexShrink: 0,
    }}>

      {/* Sidebar toggle */}
      <button
        className="js-sidebar-toggle"
        onClick={onSidebarToggle}
        style={{
          background: 'none', border: 'none',
          color: '#7d8fa3', cursor: 'pointer',
          padding: '6px', borderRadius: 6,
          display: 'flex', alignItems: 'center', flexShrink: 0,
        }}
      >
        <Icon.Menu size={18} />
      </button>

      {/* Workspace switcher */}
      <div ref={wsRef} style={{ position: 'relative' }}>
        <button
          onClick={() => setWsOpen(o => !o)} className="sp-btn-ws"
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: '#f8fafc', border: '1px solid #d1d9e0',
            borderRadius: 8, padding: '5px 10px',
            cursor: 'pointer', color: '#334155', fontSize: '0.82rem', fontWeight: 500,
          }}
        >
          <Icon.Layers size={13} color="#7d8fa3" />
          <span>{currentWs?.name ?? 'Workspace'}</span>
          <Icon.ChevronDown size={13} color="#7d8fa3" />
        </button>

        {wsOpen && (
          <div style={{
            position: 'absolute', left: 0, top: 'calc(100% + 6px)',
            background: '#fff', border: '1px solid #d1d9e0',
            borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
            minWidth: 200, zIndex: 1000, padding: '6px 0',
          }}>
            <p style={{ fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#7d8fa3', margin: 0, padding: '6px 16px 8px' }}>
              Switch workspace
            </p>

            {workspaces.length === 0 && (
              <p style={{ fontSize: '0.82rem', color: '#7d8fa3', margin: 0, padding: '4px 16px 10px' }}>
                No workspaces yet
              </p>
            )}

            {workspaces.map(ws => (
              <button
                key={ws.id}
                onClick={() => selectWorkspace(ws)}
                style={{
                  ...dropItem,
                  fontWeight: currentWs?.id === ws.id ? 500 : 400,
                  color: currentWs?.id === ws.id ? '#0f172a' : '#334155',
                }}
              >
                <Icon.Check
                  size={13}
                  style={{ marginRight: 8, opacity: currentWs?.id === ws.id ? 1 : 0, color: '#84cc16', flexShrink: 0 }}
                />
                {ws.name}
              </button>
            ))}

            <div style={{ borderTop: '1px solid #f1f5f9', margin: '4px 0' }} />
            <button
              onClick={() => { navigate('/workspaces'); setWsOpen(false); }}
              className="sp-drop-item" style={{ ...dropItem, color: '#64748b' }}
            >
              <Icon.Settings size={13} style={{ marginRight: 8, color: '#7d8fa3' }} />
              Manage workspaces
            </button>
          </div>
        )}
      </div>

      {/* Spacer */}
      <div style={{ flexGrow: 1 }} />

      {/* Avatar + user dropdown */}
      <div ref={avatarRef} style={{ position: 'relative' }}>
        <button
          onClick={() => setAvatarOpen(o => !o)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '4px 8px', borderRadius: 8,
          }}
        >
          {myContext.picture ? (
            <img
              src={myContext.picture}
              alt=""
              style={{
                width: 30, height: 30, borderRadius: '50%',
                objectFit: 'cover', border: '2px solid #d1d9e0',
              }}
            />
          ) : (
            <div style={{
              width: 30, height: 30, borderRadius: '50%',
              background: '#f1f5f9', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
            }}>
              <Icon.User size={14} color="#7d8fa3" />
            </div>
          )}
          <span style={{ fontSize: '0.85rem', color: '#334155', fontWeight: 500 }}>
            {myContext.name}
          </span>
          <Icon.ChevronDown size={14} color="#7d8fa3" />
        </button>

        {avatarOpen && (
          <div style={{
            position: 'absolute', right: 0, top: 'calc(100% + 6px)',
            background: '#fff', border: '1px solid #d1d9e0',
            borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
            minWidth: 188, zIndex: 1000, padding: '6px 0',
          }}>
            <div style={{ padding: '10px 16px', borderBottom: '1px solid #f1f5f9' }}>
              <p style={{ fontSize: '0.82rem', fontWeight: 500, color: '#0f172a', margin: 0 }}>
                {myContext.name}
              </p>
            </div>

            <button
              onClick={() => { navigate('/settings'); setAvatarOpen(false); }}
              className="sp-drop-item" style={dropItem}
            >
              <Icon.Settings size={14} style={{ marginRight: 8, color: '#7d8fa3' }} />
              Settings
            </button>

            <div style={{ borderTop: '1px solid #f1f5f9', margin: '4px 0' }} />

            <button onClick={handleLogout} className="sp-drop-item" style={{ ...dropItem, color: '#dc2626' }}>
              <Icon.LogOut size={14} style={{ marginRight: 8, color: '#dc2626' }} />
              Log out
            </button>
          </div>
        )}
      </div>
    </nav>
  );
}

export default TopMenu;
